import { useEffect, useMemo, useRef, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  HelpCircle, Send, Plus, Loader2, MessageSquare, Paperclip,
  Clock, Download, X, ArrowLeft, RefreshCw,
} from "lucide-react";

const PRIORITIES = ["low", "normal", "high", "urgent"];
const CATEGORIES = [
  { v: "general", l: "General Question" },
  { v: "artwork", l: "Artwork" },
  { v: "production", l: "Production" },
  { v: "shipping", l: "Shipping" },
  { v: "billing", l: "Billing" },
  { v: "brand_vault", l: "Brand Vault" },
  { v: "other", l: "Other" },
];

const STATUS_COLORS = {
  open: { bg: "rgba(200,162,74,0.14)", fg: "#946600" },
  in_progress: { bg: "rgba(61,127,156,0.14)", fg: "#3d7f9c" },
  waiting_client: { bg: "rgba(90,90,90,0.14)", fg: "#5a5a5a" },
  resolved: { bg: "rgba(74,124,62,0.16)", fg: "#1a4a1a" },
  closed: { bg: "rgba(0,0,0,0.08)", fg: "#666" },
};

const PortalSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list"); // list | new | detail
  const [activeId, setActiveId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/portal/support/tickets")
      .then((r) => { setTickets(r.data.tickets || []); setLabels(r.data.status_labels || {}); })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <PortalLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Portal</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-support-title">
            Support Center
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Every conversation with the FLOWTALIS Client Success team in one place.
            Open a new ticket or follow up on an existing one.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="ft-btn-ghost" data-testid="portal-support-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          {mode === "list" && (
            <button
              onClick={() => setMode("new")}
              className="ft-btn-primary"
              data-testid="portal-support-new"
            >
              <Plus size={14} /> New Ticket
            </button>
          )}
        </div>
      </div>

      {mode === "new" && (
        <NewTicket onCancel={() => setMode("list")} onCreated={(t) => { setActiveId(t.id); setMode("detail"); load(); }} />
      )}

      {mode === "detail" && activeId && (
        <TicketDetail
          ticketId={activeId}
          onBack={() => { setMode("list"); setActiveId(null); load(); }}
        />
      )}

      {mode === "list" && (
        loading ? (
          <div className="ft-card fx-skeleton h-64" />
        ) : tickets.length === 0 ? (
          <div className="ft-card text-center py-16" data-testid="portal-support-empty">
            <HelpCircle size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
            <h3 className="text-lg font-semibold">No support tickets yet.</h3>
            <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
              Need help? Open a ticket and our Client Success team will respond
              within one business day.
            </p>
            <button onClick={() => setMode("new")} className="ft-btn-primary mt-6" data-testid="portal-support-new-empty">
              <Plus size={14} /> Open Your First Ticket
            </button>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="portal-support-list">
            {tickets.map((t) => {
              const sc = STATUS_COLORS[t.status] || STATUS_COLORS.open;
              return (
                <button
                  key={t.id}
                  onClick={() => { setActiveId(t.id); setMode("detail"); }}
                  className="ft-card text-left hover:border-[var(--ft-gold)] transition-colors"
                  data-testid={`portal-support-ticket-${t.id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="ft-eyebrow">{t.ticket_number} · {t.category}</div>
                      <h3 className="text-lg font-bold tracking-tight mt-1">{t.subject}</h3>
                      <div className="text-xs text-[var(--ft-muted)] mt-1">
                        Opened {new Date(t.created_at).toLocaleDateString()} ·
                        {" "}Last update {new Date(t.updated_at).toLocaleString()} ·
                        {" "}{(t.messages || []).length} message(s)
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ background: sc.bg, color: sc.fg }}
                      >
                        {labels[t.status] || t.status}
                      </span>
                      <span className="text-[0.65rem] uppercase tracking-[0.16em] text-[var(--ft-muted)]">
                        {t.priority}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </PortalLayout>
  );
};

const NewTicket = ({ onCancel, onCreated }) => {
  const [form, setForm] = useState({ subject: "", category: "general", priority: "normal", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.subject.trim() || form.subject.trim().length < 3) return toast.error("Subject is required");
    if (!form.message.trim() || form.message.trim().length < 3) return toast.error("Please describe your request");
    setSubmitting(true);
    try {
      const r = await api.post("/portal/support/tickets", form);
      toast.success("Ticket submitted. We'll respond shortly.");
      onCreated(r.data.ticket);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit ticket");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="ft-card space-y-5 max-w-2xl" data-testid="portal-support-new-form">
      <div>
        <div className="ft-eyebrow mb-2">New Support Ticket</div>
        <p className="text-sm text-[var(--ft-muted)]">
          Tell us what&apos;s going on. Include as much detail as possible so we can help you faster.
        </p>
      </div>
      <div>
        <label className="ft-label block mb-1.5">Subject *</label>
        <input
          className="ft-input"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          data-testid="portal-support-subject"
        />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="ft-label block mb-1.5">Category</label>
          <select
            className="ft-input"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            data-testid="portal-support-category"
          >
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div>
          <label className="ft-label block mb-1.5">Priority</label>
          <select
            className="ft-input"
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            data-testid="portal-support-priority"
          >
            {PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="ft-label block mb-1.5">Describe your request *</label>
        <textarea
          className="ft-input min-h-[140px]"
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          placeholder="Include order/brand references, timeline, expected outcome…"
          data-testid="portal-support-message"
        />
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="ft-btn-ghost" data-testid="portal-support-cancel">Cancel</button>
        <button onClick={submit} disabled={submitting} className="ft-btn-primary" data-testid="portal-support-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit Ticket
        </button>
      </div>
    </div>
  );
};

const TicketDetail = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;

  const load = () => {
    setLoading(true);
    api.get(`/portal/support/tickets/${ticketId}`)
      .then((r) => setTicket(r.data.ticket))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [ticketId]);

  const submitReply = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await api.post(`/portal/support/tickets/${ticketId}/messages`, { body: reply });
      setReply("");
      toast.success("Reply posted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reply");
    } finally { setPosting(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/portal/support/tickets/${ticketId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attachment added.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const messages = useMemo(() => (ticket?.messages || []).filter((m) => !m.internal), [ticket]);

  const sc = ticket ? (STATUS_COLORS[ticket.status] || STATUS_COLORS.open) : null;
  const isClosed = ticket && ["closed"].includes(ticket.status);

  return (
    <div>
      <button onClick={onBack} className="ft-btn-ghost mb-4" data-testid="portal-support-back">
        <ArrowLeft size={14} /> Back to tickets
      </button>
      {loading || !ticket ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : (
        <div className="grid gap-4" data-testid="portal-support-detail">
          <div className="ft-card">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="ft-eyebrow">{ticket.ticket_number} · {ticket.category}</div>
                <h2 className="text-2xl font-bold tracking-tight mt-1">{ticket.subject}</h2>
                <div className="text-xs text-[var(--ft-muted)] mt-2">
                  Opened {new Date(ticket.created_at).toLocaleString()}
                </div>
              </div>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                style={{ background: sc.bg, color: sc.fg }}
              >
                {ticket.status_label || ticket.status}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`ft-card ${m.author_side === "admin" ? "border-l-4 border-l-[var(--ft-gold)]" : ""}`}
                data-testid={`portal-support-msg-${m.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold">
                    {m.author_name}
                    <span className="text-[var(--ft-muted)] font-normal ml-2 text-xs">
                      {m.author_side === "admin" ? "FLOWTALIS Team" : "You"}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--ft-muted)]">
                    <Clock size={10} className="inline mr-1" />
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-sm text-[var(--ft-charcoal)] whitespace-pre-wrap">{m.body}</div>
                {m.attachments && m.attachments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--ft-border)] space-y-1.5">
                    {m.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={`${API}/portal/support/attachments/${ticket.id}/${a.id}/download?auth=${encodeURIComponent(token || "")}`}
                        download={a.original_filename}
                        className="inline-flex items-center gap-2 text-xs text-[var(--ft-gold)] hover:underline"
                        data-testid={`portal-support-att-${a.id}`}
                      >
                        <Paperclip size={12} /> {a.original_filename}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {!isClosed && (
            <div className="ft-card" data-testid="portal-support-reply">
              <div className="ft-eyebrow mb-2">Post a reply</div>
              <textarea
                className="ft-input min-h-[100px]"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply…"
                data-testid="portal-support-reply-input"
              />
              <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => uploadFile(e.target.files?.[0])}
                    data-testid="portal-support-file-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="ft-btn-ghost"
                    data-testid="portal-support-file-btn"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Attach file
                  </button>
                </div>
                <button
                  onClick={submitReply}
                  disabled={posting || !reply.trim()}
                  className="ft-btn-primary"
                  data-testid="portal-support-post"
                >
                  {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post Reply
                </button>
              </div>
            </div>
          )}
          {isClosed && (
            <div className="ft-card text-sm text-[var(--ft-muted)]" data-testid="portal-support-closed">
              This ticket is closed. Need to follow up? Open a new ticket.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSupport;
